import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import '../../styles/sections/contact.css';

// ─────────────────────────────────────────────
//  ⚙️  Replace with your real reCAPTCHA Site Key
//  Get it from: https://www.google.com/recaptcha/admin
// ─────────────────────────────────────────────
const RECAPTCHA_SITE_KEY = '6LfkLH8sAAAAANj7gZ8N4ZFVDeo3WmYgZd1_Qckt';

const PRODUCTS = [
  { name: 'robots', value: 'truuna',       label: 'Truuna'       },
  { name: 'robots', value: 'dms',         label: 'Amrrita'      },
  { name: 'robots', value: 'suru',         label: 'Suru'         },
  { name: 'robots', value: 'seva',         label: 'Seva'         },
  { name: 'robots', value: 'gadai',        label: 'Gadai'        },
  { name: 'robots', value: 'gaadv',        label: 'Gaadv'        },
  { name: 'robots', value: 'magic_mirror', label: 'Magic Mirror' },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    address: '',
    message: '',
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [captchaToken, setCaptchaToken]         = useState(null);
  const [status, setStatus]                     = useState('idle'); // idle | loading | success | error
  const [statusMessage, setStatusMessage]       = useState('');

  const recaptchaRef = useRef(null);

  // ── Auto-dismiss status message after 3.5 seconds ─
  useEffect(() => {
    if (status === 'success' || status === 'error') {
      const timer = setTimeout(() => {
        setStatus('idle');
        setStatusMessage('');
      }, 3500); // 3.5 seconds

      return () => clearTimeout(timer); // Cleanup timeout on unmount or status change
    }
  }, [status]);

  // ── Handlers ─────────────────────────────────
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProductToggle = (value) => {
    setSelectedProducts((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  // ── Submit ────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (!formData.name.trim())              return alert('Please enter your name.');
    if (!formData.phone.trim())             return alert('Please enter your phone number.');
    if (!formData.email.trim())             return alert('Please enter your email.');
    if (!formData.company.trim())           return alert('Please enter your company name.');
    if (!formData.address.trim())           return alert('Please enter your address.');
    if (selectedProducts.length === 0)      return alert('Please select at least one product.');
    if (!formData.message.trim())           return alert('Please enter a message.');
    if (!captchaToken)                      return alert('Please complete the reCAPTCHA verification.');

    setStatus('loading');
    setStatusMessage('');

    const payload = {
      name:          formData.name.trim(),
      phone:         formData.phone.trim(),
      email:         formData.email.trim(),
      company:       formData.company.trim(),
      address:       formData.address.trim(),
      products:      selectedProducts,
      message:       formData.message.trim(),
      captcha_token: captchaToken,
    };

    try {
      const response = await fetch('/api/send_mail.php', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setStatus('success');
        setStatusMessage('✅ Your message has been sent! We will get back to you shortly.');
        // Reset form
        setFormData({ name: '', phone: '', email: '', company: '', address: '', message: '' });
        setSelectedProducts([]);
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      } else {
        setStatus('error');
        setStatusMessage(`❌ ${data.message || 'Something went wrong. Please try again.'}`);
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      }
    } catch (err) {
      setStatus('error');
      setStatusMessage('❌ Network error. Please check your connection and try again.');
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  // ── Render ────────────────────────────────────
  return (
    <div className="contact-page">
      <div className="contact-header">
        <h1 className="contact-title">
          <span className="contact-icon">✉️</span> Contact Us
        </h1>
        <p className="contact-subtitle">
          Get in touch with our team to discuss your electronics development project.
        </p>
      </div>

      <div className="contact-container">
        {/* ── FORM SECTION ── */}
        <div className="contact-form-section">
          <div className="form-header">
            <h2 className="form-title">
              <span className="message-icon">💬</span> Send us a message
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="contact-form" noValidate>

            {/* Row 1: Name | Phone */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  <span className="label-icon">👤</span> NAME <span className="required">*</span>
                </label>
                <input
                  type="text" id="name" name="name"
                  value={formData.name} onChange={handleChange}
                  placeholder="Your name" className="form-input" required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  <span className="label-icon">📱</span> PHONE NO <span className="required">*</span>
                </label>
                <input
                  type="tel" id="phone" name="phone"
                  value={formData.phone} onChange={handleChange}
                  placeholder="+91 1234567890" className="form-input" required
                />
              </div>
            </div>

            {/* Row 2: Email | Company */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  <span className="label-icon">📧</span> EMAIL <span className="required">*</span>
                </label>
                <input
                  type="email" id="email" name="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="your@email.com" className="form-input" required
                />
              </div>
              <div className="form-group">
                <label htmlFor="company" className="form-label">
                  <span className="label-icon">🏢</span> COMPANY <span className="required">*</span>
                </label>
                <input
                  type="text" id="company" name="company"
                  value={formData.company} onChange={handleChange}
                  placeholder="Your company" className="form-input" required
                />
              </div>
            </div>

            {/* Row 3: Address (full width) */}
            <div className="form-group">
              <label htmlFor="address" className="form-label">
                <span className="label-icon">📍</span> ADDRESS <span className="required">*</span>
              </label>
              <input
                type="text" id="address" name="address"
                value={formData.address} onChange={handleChange}
                placeholder="Your full address" className="form-input" required
              />
            </div>

            {/* Row 4: Products */}
            <div className="form-group">
              <label className="form-label section-label">
                <span className="label-icon">🤖</span> WHICH PRODUCT DO YOU NEED <span className="required">*</span> ❓
              </label>
              <div className="checkbox-grid">
                {PRODUCTS.map((product) => (
                  <label key={product.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="robots"
                      value={product.value}
                      checked={selectedProducts.includes(product.value)}
                      onChange={() => handleProductToggle(product.value)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">{product.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Row 5: Message (full width) */}
            <div className="form-group">
              <label htmlFor="message" className="form-label">
                <span className="label-icon">✏️</span> MESSAGE <span className="required">*</span>
              </label>
              <textarea
                id="message" name="message"
                value={formData.message} onChange={handleChange}
                placeholder="Tell us about your project..."
                className="form-textarea" rows="5" required
              />
            </div>

            {/* Row 6: reCAPTCHA */}
            <div className="form-group recaptcha-group">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                onExpired={handleCaptchaExpired}
              />
            </div>

            {/* Status Message */}
            {status !== 'idle' && status !== 'loading' && (
              <div className={`form-status form-status--${status}`}>
                {statusMessage}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-button"
              disabled={status === 'loading'}
            >
              <span className="button-icon">📮</span>
              {status === 'loading' ? 'SENDING…' : 'SEND MESSAGE'}
            </button>

          </form>
        </div>

        {/* ── INFO SECTION (unchanged) ── */}
        <div className="contact-info-section">
          <div className="info-header">
            <h2 className="info-title">
              <span className="phone-icon">📞</span> Get in touch
            </h2>
          </div>

          <div className="info-content">
            <div className="info-card">
              <h3 className="info-card-title"><span className="card-icon">📧</span> Email</h3>
              <a href="mailto:contact@aashman.in" className="info-link">contact@aashman.in</a>
            </div>

            <div className="info-card">
              <h3 className="info-card-title"><span className="card-icon">🏢</span> Office</h3>
              <p className="info-text">Aashman Technicals Pvt. Ltd.</p>
              <p className="info-text">Sr.No. 81/4, Shri. Guru Datta Mandit Lane, Dangat Industrial Estate,</p>
              <p className="info-text">2nd Kamaan, Shivane, Pune</p>
              <p className="info-text">Maharashtra - 411058</p>
              <p className="info-text">India</p>
              <a href="https://maps.app.goo.gl/JSY2WZGeRfcxnckr6" target="_blank" rel="noopener noreferrer" className="map-link">
                🗺️ View on Map
              </a>
            </div>

            <div className="info-card">
              <h3 className="info-card-title"><span className="card-icon">📞</span> Phone</h3>
              <a href="tel:+919370329886" className="info-link">+91 9370329886</a>
              <a href="tel:+917499059195" className="info-link">+91 7499059195</a>
            </div>

            <div className="info-card">
              <h3 className="info-card-title"><span className="card-icon">🕑</span> Business Hours</h3>
              <p className="info-text">Monday — Saturday: 09:30 AM — 07:00 PM</p>
              <p className="info-text">Sunday: Closed</p>
            </div>

            <div className="info-card">
              <h3 className="info-card-title"><span className="card-icon">🌐</span> Connect</h3>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/112030326/" target="_blank" rel="noopener noreferrer" className="social-button social-button-linkedin">
                  <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/ashmantechnical/" target="_blank" rel="noopener noreferrer" className="social-button social-button-instagram">
                  <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.645.069-4.85.069-3.204 0-3.584-.011-4.849-.069-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.647 2.163 15.268 2.163 12s.012-3.584.07-4.849c.062-1.366.334-2.633 1.308-3.608.975-.975 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zM12 0C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.986 8.741 24 12 24s3.668-.014 4.948-.072c1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.668-.072-4.948-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a href="https://wa.me/919370329886" target="_blank" rel="noopener noreferrer" className="social-button social-button-whatsapp">
                  <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
                <a href="mailto:contact@aashman.in" className="social-button social-button-email">
                  <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Contact;